import { useState } from 'react'
import { FileText, User, Building, Mail, Check, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDiscoveryContext } from '../DiscoveryWizard'
import { ProposalPDF } from '../ProposalPDF'
import { formatPrice } from '@/lib/pricing-data'
import { formatCurrency, formatPercent, formatPayback } from '@/lib/value-calculator'
import { useCases } from '@/lib/discovery-data'

export function SummaryStep() {
  const {
    state,
    valueMetrics,
    quoteItems,
    totalInvestment,
    setCustomerInfo,
  } = useDiscoveryContext()
  const [showPDF, setShowPDF] = useState(false)

  const useCase = useCases.find((u) => u.id === state.useCase)
  const validUntil = new Date()
  validUntil.setDate(validUntil.getDate() + 30)

  return (
    <>
      <div className="h-full flex flex-col items-center px-6 py-12 overflow-auto">
        <div className="max-w-4xl w-full space-y-8">
          {/* Header */}
          <div className="text-center wizard-fade-in">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
              Your proposal is ready
            </h2>
            <p className="text-muted-foreground">
              Review and export your customized business case
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Customer info */}
            <div className="space-y-6">
              <div className="p-6 rounded-lg border border-border/30 bg-secondary/10 wizard-fade-in">
                <h3 className="text-sm font-medium text-foreground mb-4">
                  Customer Details
                </h3>
                <div className="space-y-4">
                  <CustomerInput
                    icon={<User className="w-4 h-4" />}
                    label="Contact Name"
                    placeholder="Enter contact name"
                    value={state.customerName}
                    onChange={(val) => setCustomerInfo({ name: val })}
                    required
                  />
                  <CustomerInput
                    icon={<Building className="w-4 h-4" />}
                    label="Company"
                    placeholder="Enter company name"
                    value={state.customerCompany}
                    onChange={(val) => setCustomerInfo({ company: val })}
                  />
                  <CustomerInput
                    icon={<Mail className="w-4 h-4" />}
                    label="Email"
                    placeholder="Enter email address"
                    value={state.customerEmail}
                    onChange={(val) => setCustomerInfo({ email: val })}
                    type="email"
                  />
                </div>
              </div>

              {/* Quote validity */}
              <div className="p-4 rounded-lg border border-border/20 flex items-center gap-3 wizard-fade-in">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Quote valid until</p>
                  <p className="text-sm font-medium text-foreground">
                    {validUntil.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Summary */}
            <div className="space-y-4">
              {/* Value highlights */}
              {valueMetrics && (
                <div className="p-6 rounded-lg border border-primary/30 bg-primary/5 wizard-fade-in">
                  <h3 className="text-sm font-medium text-muted-foreground mb-4">
                    Value Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <ValueHighlight
                      label="Annual Savings"
                      value={formatCurrency(valueMetrics.projectedSavings)}
                    />
                    <ValueHighlight
                      label="Success Rate"
                      value={formatPercent(valueMetrics.projectedSuccessRate)}
                    />
                    <ValueHighlight
                      label="3-Year ROI"
                      value={`${valueMetrics.threeYearROI}%`}
                    />
                    <ValueHighlight
                      label="Payback Period"
                      value={formatPayback(valueMetrics.paybackPeriodMonths)}
                    />
                  </div>
                </div>
              )}

              {/* Investment breakdown */}
              <div className="p-6 rounded-lg border border-border/30 bg-secondary/10 wizard-fade-in">
                <h3 className="text-sm font-medium text-muted-foreground mb-4">
                  Investment Breakdown
                </h3>
                <div className="space-y-2">
                  {quoteItems.map((qi) => (
                    <div key={qi.item.id} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{qi.item.name}</span>
                      <span className="text-foreground font-medium">
                        {formatPrice(qi.item.price * qi.quantity)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-border/30 pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-foreground">Total Investment</span>
                      <span className="text-xl font-bold text-primary">
                        {formatCurrency(totalInvestment)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Use case context */}
              {useCase && (
                <div className="p-4 rounded-lg border border-border/20 wizard-fade-in">
                  <p className="text-xs text-muted-foreground mb-1">Configured for</p>
                  <p className="text-sm font-medium text-foreground">{useCase.name}</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">{useCase.tagline}</p>
                </div>
              )}
            </div>
          </div>

          {/* Export button */}
          <div className="flex justify-center pt-4 wizard-fade-in">
            <button
              onClick={() => setShowPDF(true)}
              disabled={!state.customerName.trim()}
              className={cn(
                'flex items-center gap-3 px-8 py-4 rounded-lg text-lg font-medium transition-all',
                'bg-primary text-primary-foreground hover:bg-primary/90',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                state.customerName.trim() && 'wizard-pulse'
              )}
            >
              <FileText className="w-5 h-5" />
              Generate Proposal PDF
            </button>
          </div>

          {!state.customerName.trim() && (
            <p className="text-center text-xs text-muted-foreground/50">
              Enter a customer name to generate the proposal
            </p>
          )}

          {/* What's included in PDF */}
          <div className="text-center text-xs text-muted-foreground/60 space-y-1 wizard-fade-in">
            <p className="font-medium">Your proposal will include:</p>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              {[
                'Executive Summary',
                'Situation Analysis',
                'Solution Fit',
                'Value Projection',
                'Investment Breakdown',
                'Next Steps',
              ].map((item) => (
                <span key={item} className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-primary" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* PDF Modal */}
      {showPDF && <ProposalPDF onClose={() => setShowPDF(false)} />}
    </>
  )
}

interface CustomerInputProps {
  icon: React.ReactNode
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  type?: string
}

function CustomerInput({
  icon,
  label,
  placeholder,
  value,
  onChange,
  required,
  type = 'text',
}: CustomerInputProps) {
  return (
    <div>
      <label className="flex items-center gap-2 text-xs text-muted-foreground/70 mb-1.5">
        {icon}
        {label}
        {required && <span className="text-destructive">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'w-full bg-transparent border-b-2 border-border/40 py-2',
          'text-foreground placeholder:text-muted-foreground/40',
          'focus:border-primary focus:outline-none transition-colors'
        )}
      />
    </div>
  )
}

interface ValueHighlightProps {
  label: string
  value: string
}

function ValueHighlight({ label, value }: ValueHighlightProps) {
  return (
    <div>
      <p className="text-xs text-muted-foreground/70">{label}</p>
      <p className="text-lg font-bold text-primary">{value}</p>
    </div>
  )
}

